export enum ElementType {
  EMPTY = 'EMPTY',
  WALL = 'WALL',
  SAND = 'SAND',
  WATER = 'WATER',
  STONE = 'STONE',
  WOOD = 'WOOD',
  FIRE = 'FIRE',
  SMOKE = 'SMOKE',
  PLANT = 'PLANT',
  ACID = 'ACID',
  TORNADO = 'TORNADO',
  STEAM = 'STEAM',
  ICE = 'ICE',
  GLASS = 'GLASS',
  MUD = 'MUD',
  DIRT = 'DIRT',
  FLOWER = 'FLOWER',
  LAVA = 'LAVA',
  LIGHTNING = 'LIGHTNING',
  OIL = 'OIL',
  GUNPOWDER = 'GUNPOWDER',
  METAL = 'METAL',
  EMBER = 'EMBER',
  METEOR = 'METEOR',
  BOMB = 'BOMB',
  BLACKHOLE = 'BLACKHOLE',
  RAINCLOUD = 'RAINCLOUD',
  // NEW ELEMENTS
  WAX = 'WAX',
  FUSE = 'FUSE',
  COAL = 'COAL',
  SALT = 'SALT',
  SLIME = 'SLIME',
  MAGMA = 'MAGMA',
  ELECTRIC_SPARK = 'ELECTRIC_SPARK',
  // NEW SPELLS
  FREEZE_RAY = 'FREEZE_RAY',
  EARTHQUAKE = 'EARTHQUAKE',
  TSUNAMI = 'TSUNAMI',
}

export interface Element {
  id: ElementType | string;
  name: string;
  color: string;
  description: string;
  isCustom?: boolean;
  flammable?: boolean;
  density?: number;
  thermalConductivity?: number;
}

export interface Particle {
  elementId: ElementType | string;
  lifetime?: number;
  vx?: number;
  vy?: number;
  temperature: number;
  age?: number;
}

export type Grid = Particle[][];

export interface TornadoAgent {
  id: number;
  type: 'TORNADO';
  x: number; // center x in grid coords
  y: number; // center y in grid coords
  vx: number; // horizontal velocity
  vy: number; // vertical velocity
  radius: number;
  lifetime: number;
  strength: number;
  rotation: number; // For swirling effect
}

export interface LightningAgent {
  id: number;
  type: 'LIGHTNING';
  path: { x: number; y: number }[];
  lifetime: number;
}

export interface MeteorAgent {
  id: number;
  type: 'METEOR';
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  lifetime: number;
}

export interface BlackHoleAgent {
  id: number;
  type: 'BLACKHOLE';
  x: number;
  y: number;
  radius: number;
  strength: number;
  lifetime: number;
}

export interface RainCloudAgent {
  id: number;
  type: 'RAINCLOUD';
  x: number;
  y: number;
  vx: number;
  width: number;
  lifetime: number;
}

export interface ExplosionAgent {
  id: number;
  type: 'EXPLOSION';
  x: number;
  y: number;
  radius: number;
  maxRadius: number;
  lifetime: number;
}

export interface BombAgent {
  id: number;
  type: 'BOMB_AGENT';
  x: number;
  y: number;
  vy: number;
  timer: number;
  maxTimer: number;
}

// NEW SPELL AGENTS
export interface FreezeRayAgent {
  id: number;
  type: 'FREEZE_RAY';
  path: { x: number; y: number }[];
  lifetime: number;
}

export interface EarthquakeAgent {
  id: number;
  type: 'EARTHQUAKE';
  x: number;
  y: number;
  radius: number;
  maxRadius: number;
  lifetime: number;
  intensity: number;
}

export interface TsunamiAgent {
  id: number;
  type: 'TSUNAMI';
  x: number;
  y: number;
  vx: number;
  width: number;
  height: number;
  lifetime: number;
}

export type Agent = 
  | TornadoAgent 
  | LightningAgent 
  | MeteorAgent 
  | BlackHoleAgent 
  | RainCloudAgent 
  | ExplosionAgent 
  | BombAgent
  | FreezeRayAgent
  | EarthquakeAgent
  | TsunamiAgent;

// ============ PERFORMANCE OPTIMIZATION TYPES ============

/** Represents a chunk of the grid for optimized processing */
export interface Chunk {
  x: number; // chunk x index
  y: number; // chunk y index
  isActive: boolean; // whether chunk needs processing
  sleepCounter: number; // frames without changes
  lastActivityFrame: number; // frame number of last activity
  modifiedCells: Set<string>; // cells that changed this frame ("x,y" format)
}

/** Dirty rectangle for optimized rendering */
export interface DirtyRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Chunk manager state */
export interface ChunkManager {
  chunks: Chunk[][];
  chunkWidth: number;
  chunkHeight: number;
  chunksX: number;
  chunksY: number;
  activeChunkCount: number;
  sleepingChunkCount: number;
  dirtyRects: DirtyRect[];
  frameCount: number;
}

/** Performance metrics for monitoring */
export interface PerformanceMetrics {
  lastFrameTime: number;
  avgFrameTime: number;
  frameTimeHistory: number[];
  activeChunks: number;
  sleepingChunks: number;
  particlesProcessed: number;
  dirtyRectsCount: number;
}

/** Object pool for particle reuse */
export interface ParticlePool {
  pool: Particle[];
  maxSize: number;
  acquired: number;
  released: number;
}

// ============ SETTINGS TYPES ============

export interface Settings {
  audio: {
    enabled: boolean;
    volume: number;
  };
  graphics: {
    glowEffects: boolean;
    particleLimit: number;
  };
  simulation: {
    speed: number;
  };
}
